import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { Modal, Spinner } from 'react-bootstrap';
import { FiX, FiArrowRight, FiArrowLeft, FiMove, FiAlertCircle, FiPlus } from 'react-icons/fi';
import { BsFiletypeCsv } from 'react-icons/bs';
import { IoDownload } from 'react-icons/io5';
import { rmsApi } from '../../../../state/axios';
import { exportRefreshEventEmitter } from '../../services/exportRefreshEventEmitter';
import type { RootState } from '../../../../state/store';

// Column type matching backend response (snake_case)
export interface CustomExportColumn {
  id: string;
  label: string;
  column_type: 'dimension' | 'value';
  aggregation: string | null;
  sql_expression: string;
  sql_alias: string;
  description?: string;
}

interface CustomExportDialogProps {
  show: boolean;
  onClose: () => void;
  onExport: (selectedColumns: CustomExportColumn[], reportName: string) => void;
  isExporting?: boolean;
}

const MOBILE_BREAKPOINT = 720;

const CustomExportDialog: React.FC<CustomExportDialogProps> = ({
  show,
  onClose,
  onExport,
  isExporting = false
}) => {
  const routeMatch = useParams();
  const navigate = useNavigate();
  const theme = useSelector((state: RootState) => state.theme.mode);
  const isDark = theme !== 'light';
  const [allColumns, setAllColumns] = useState<CustomExportColumn[]>([]);
  const [availableColumns, setAvailableColumns] = useState<CustomExportColumn[]>([]);
  const [selectedColumns, setSelectedColumns] = useState<CustomExportColumn[]>([]);
  const [draggedItem, setDraggedItem] = useState<CustomExportColumn | null>(null);
  const [dragSource, setDragSource] = useState<'available' | 'selected' | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [dragOverItemIndex, setDragOverItemIndex] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reportName, setReportName] = useState('');
  const [hasInitiatedExport, setHasInitiatedExport] = useState(false);
  const [exportCompleted, setExportCompleted] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Touch drag-and-drop refs
  const touchInfoRef = useRef<{
    x: number;
    y: number;
    column: CustomExportColumn;
    source: 'available' | 'selected';
  } | null>(null);
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isDraggingTouchRef = useRef(false);
  const ghostRef = useRef<HTMLDivElement | null>(null);

  // Track viewport width for mobile layout
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Reset export state when dialog opens
  useEffect(() => {
    if (show) {
      setHasInitiatedExport(false);
      setExportCompleted(false);
    }
  }, [show]);

  // Listen for export completion events
  useEffect(() => {
    if (!hasInitiatedExport) return;

    const handleExportCompleted = () => {
      setExportCompleted(true);
    };

    exportRefreshEventEmitter.onExportCompleted(handleExportCompleted);

    return () => {
      exportRefreshEventEmitter.offExportCompleted(handleExportCompleted);
    };
  }, [hasInitiatedExport]);

  // Generate default filename when dialog opens
  useEffect(() => {
    if (show && !reportName) {
      const timestamp = new Date().toISOString().split('T')[0];
      setReportName(`${routeMatch?.widget}-report-${timestamp}`);
    }
  }, [show]);

  // Fetch columns when dialog opens
  useEffect(() => {
    if (show && allColumns.length === 0) {
      fetchColumns();
    }
  }, [show]);

  // Cleanup touch drag on unmount
  useEffect(() => {
    return () => {
      if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
      if (ghostRef.current?.parentNode) {
        ghostRef.current.parentNode.removeChild(ghostRef.current);
      }
    };
  }, []);

  const fetchColumns = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const dashboard = routeMatch?.dashboard;
      const widget = routeMatch?.widget;
      const url = `/api/v1/dar/${dashboard}/${widget}/report/custom-export-columns`;

      const response = await rmsApi.get(url);
      const columns = response.data.columns || [];

      setAllColumns(columns);
      setAvailableColumns(columns);
    } catch (err: any) {
      console.error('Failed to fetch custom export columns:', err);
      const errorMsg = err.response?.data?.message || err.response?.data?.error;
      setError(errorMsg || 'Failed to load export columns');
    } finally {
      setIsLoading(false);
    }
  };

  // --- Desktop HTML5 drag handlers ---

  const handleDragStart = (
    e: React.DragEvent,
    column: CustomExportColumn,
    source: 'available' | 'selected'
  ) => {
    setDraggedItem(column);
    setDragSource(source);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', column.id);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
    setDragSource(null);
    setDragOverIndex(null);
    setDragOverItemIndex(null);
  };

  const handleDragOverBucket = (e: React.DragEvent, target: 'available' | 'selected') => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverItemIndex(null);
    if (target === 'selected' && dragSource === 'selected') {
      setDragOverIndex(selectedColumns.length);
    }
  };

  const handleDragOverItem = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.stopPropagation();
    if (!dragSource) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const isLowerHalf = e.clientY > rect.top + rect.height / 2;
    const targetIndex = isLowerHalf ? index + 1 : index;
    setDragOverIndex(targetIndex);
    setDragOverItemIndex(index);
  };

  const handleDropOnAvailable = (e: React.DragEvent) => {
    e.preventDefault();
    if (draggedItem && dragSource === 'selected') {
      setSelectedColumns((prev) => prev.filter((col) => col.id !== draggedItem.id));
      setAvailableColumns((prev) => {
        if (prev.find((col) => col.id === draggedItem.id)) return prev;
        return [...prev, draggedItem];
      });
    }
    handleDragEnd();
  };

  const handleDropOnSelected = (e: React.DragEvent) => {
    e.preventDefault();
    if (!draggedItem) return;

    if (dragSource === 'available') {
      setAvailableColumns((prev) => prev.filter((col) => col.id !== draggedItem.id));
      setSelectedColumns((prev) => {
        if (prev.find((col) => col.id === draggedItem.id)) return prev;
        return [...prev, draggedItem];
      });
    }

    if (dragSource === 'selected') {
      setSelectedColumns((prev) => {
        const currentIndex = prev.findIndex((col) => col.id === draggedItem.id);
        if (currentIndex === -1) return prev;
        const newList = [...prev];
        newList.splice(currentIndex, 1);
        newList.splice(newList.length, 0, draggedItem);
        return newList;
      });
    }

    handleDragEnd();
  };

  const handleDropOnSelectedItem = (e: React.DragEvent, targetIndex: number) => {
    e.preventDefault();
    e.stopPropagation();
    if (!draggedItem) return;
    const resolvedTargetIndex = dragOverIndex ?? targetIndex;

    if (dragSource === 'available') {
      setAvailableColumns((prev) => prev.filter((col) => col.id !== draggedItem.id));
      setSelectedColumns((prev) => {
        const newList = [...prev];
        newList.splice(resolvedTargetIndex, 0, draggedItem);
        return newList;
      });
    } else if (dragSource === 'selected') {
      setSelectedColumns((prev) => {
        const currentIndex = prev.findIndex((col) => col.id === draggedItem.id);
        if (currentIndex === -1) return prev;
        const newList = [...prev];
        newList.splice(currentIndex, 1);
        const adjustedIndex =
          resolvedTargetIndex > currentIndex ? resolvedTargetIndex - 1 : resolvedTargetIndex;
        newList.splice(adjustedIndex, 0, draggedItem);
        return newList;
      });
    }
    handleDragEnd();
  };

  // --- Touch drag-and-drop handlers (long press to initiate) ---

  const handleTouchStart = (
    e: React.TouchEvent,
    column: CustomExportColumn,
    source: 'available' | 'selected'
  ) => {
    const touch = e.touches[0];
    touchInfoRef.current = { x: touch.clientX, y: touch.clientY, column, source };

    longPressTimerRef.current = setTimeout(() => {
      if (!touchInfoRef.current) return;
      isDraggingTouchRef.current = true;
      setDraggedItem(column);
      setDragSource(source);

      const ghost = document.createElement('div');
      ghost.textContent = column.label;
      ghost.style.cssText = `
        position: fixed;
        left: ${touchInfoRef.current.x}px;
        top: ${touchInfoRef.current.y - 40}px;
        padding: 8px 16px;
        background: rgba(59, 130, 246, 0.9);
        border-radius: 6px;
        font-size: 13px;
        font-weight: 500;
        color: white;
        box-shadow: 0 4px 12px rgba(0,0,0,0.2);
        pointer-events: none;
        z-index: 10000;
        white-space: nowrap;
        max-width: 200px;
        overflow: hidden;
        text-overflow: ellipsis;
        transform: translateX(-50%);
      `;
      document.body.appendChild(ghost);
      ghostRef.current = ghost;
    }, 200);
  };

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (!touchInfoRef.current) return;

      const touch = e.touches[0];
      const dx = touch.clientX - touchInfoRef.current.x;
      const dy = touch.clientY - touchInfoRef.current.y;
      const dist = Math.sqrt(dx * dx + dy * dy);

      // If finger moved before long press triggered, cancel (user is scrolling)
      if (!isDraggingTouchRef.current && dist > 10) {
        if (longPressTimerRef.current) {
          clearTimeout(longPressTimerRef.current);
          longPressTimerRef.current = null;
        }
        touchInfoRef.current = null;
        return;
      }

      if (!isDraggingTouchRef.current) return;

      e.preventDefault(); // Prevent scroll during drag

      // Move ghost
      if (ghostRef.current) {
        ghostRef.current.style.left = `${touch.clientX}px`;
        ghostRef.current.style.top = `${touch.clientY - 40}px`;
      }

      // Detect element under finger (hide ghost temporarily)
      if (ghostRef.current) ghostRef.current.style.display = 'none';
      const el = document.elementFromPoint(touch.clientX, touch.clientY);
      if (ghostRef.current) ghostRef.current.style.display = '';

      if (!el) {
        setDragOverIndex(null);
        setDragOverItemIndex(null);
        return;
      }

      // Check if over a specific column item in the selected bucket
      const colItem = el.closest('[data-column-index]') as HTMLElement | null;
      if (colItem) {
        const bucket = colItem.closest('[data-bucket]') as HTMLElement | null;
        if (bucket?.dataset.bucket === 'selected') {
          const idx = parseInt(colItem.dataset.columnIndex!, 10);
          const rect = colItem.getBoundingClientRect();
          const isLower = touch.clientY > rect.top + rect.height / 2;
          setDragOverIndex(isLower ? idx + 1 : idx);
          setDragOverItemIndex(idx);
          return;
        }
      }

      // Check if over a bucket background
      const bucket = el.closest('[data-bucket]') as HTMLElement | null;
      if (bucket?.dataset.bucket === 'selected') {
        setDragOverIndex(selectedColumns.length);
        setDragOverItemIndex(null);
      } else {
        setDragOverIndex(null);
        setDragOverItemIndex(null);
      }
    },
    [selectedColumns.length]
  );

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
        longPressTimerRef.current = null;
      }

      if (isDraggingTouchRef.current && draggedItem) {
        e.preventDefault(); // Prevent click from firing after drag

        const touch = e.changedTouches[0];

        // Detect final drop target
        if (ghostRef.current) ghostRef.current.style.display = 'none';
        const el = document.elementFromPoint(touch.clientX, touch.clientY);
        if (ghostRef.current) ghostRef.current.style.display = '';

        if (el) {
          const bucket = el.closest('[data-bucket]') as HTMLElement | null;
          const colItem = el.closest('[data-column-index]') as HTMLElement | null;

          if (bucket?.dataset.bucket === 'selected') {
            if (colItem && dragOverIndex !== null) {
              if (dragSource === 'available') {
                setAvailableColumns((prev) => prev.filter((c) => c.id !== draggedItem.id));
                setSelectedColumns((prev) => {
                  const newList = [...prev];
                  newList.splice(dragOverIndex, 0, draggedItem);
                  return newList;
                });
              } else if (dragSource === 'selected') {
                setSelectedColumns((prev) => {
                  const ci = prev.findIndex((c) => c.id === draggedItem.id);
                  if (ci === -1) return prev;
                  const newList = [...prev];
                  newList.splice(ci, 1);
                  const adj = dragOverIndex > ci ? dragOverIndex - 1 : dragOverIndex;
                  newList.splice(adj, 0, draggedItem);
                  return newList;
                });
              }
            } else {
              if (dragSource === 'available') {
                setAvailableColumns((prev) => prev.filter((c) => c.id !== draggedItem.id));
                setSelectedColumns((prev) => {
                  if (prev.find((c) => c.id === draggedItem.id)) return prev;
                  return [...prev, draggedItem];
                });
              } else if (dragSource === 'selected') {
                setSelectedColumns((prev) => {
                  const ci = prev.findIndex((c) => c.id === draggedItem.id);
                  if (ci === -1) return prev;
                  const newList = [...prev];
                  newList.splice(ci, 1);
                  newList.push(draggedItem);
                  return newList;
                });
              }
            }
          } else if (bucket?.dataset.bucket === 'available') {
            if (dragSource === 'selected') {
              setSelectedColumns((prev) => prev.filter((c) => c.id !== draggedItem.id));
              setAvailableColumns((prev) => {
                if (prev.find((c) => c.id === draggedItem.id)) return prev;
                return [...prev, draggedItem];
              });
            }
          }
        }

        // Clean up ghost
        if (ghostRef.current?.parentNode) {
          ghostRef.current.parentNode.removeChild(ghostRef.current);
          ghostRef.current = null;
        }
        handleDragEnd();
      }

      isDraggingTouchRef.current = false;
      touchInfoRef.current = null;
    },
    [draggedItem, dragSource, dragOverIndex]
  );

  // --- Column add/remove handlers ---

  const handleAddColumn = useCallback((column: CustomExportColumn) => {
    setAvailableColumns((prev) => prev.filter((col) => col.id !== column.id));
    setSelectedColumns((prev) => [...prev, column]);
  }, []);

  const handleRemoveColumn = useCallback((column: CustomExportColumn) => {
    setSelectedColumns((prev) => prev.filter((col) => col.id !== column.id));
    setAvailableColumns((prev) => [...prev, column]);
  }, []);

  const handleAddAll = () => {
    setSelectedColumns((prev) => [...prev, ...availableColumns]);
    setAvailableColumns([]);
  };

  const handleRemoveAll = () => {
    setAvailableColumns((prev) => [...prev, ...selectedColumns]);
    setSelectedColumns([]);
  };

  const handleExport = () => {
    const finalReportName = reportName.trim();
    if (selectedColumns.length > 0 && finalReportName) {
      setHasInitiatedExport(true);
      onExport(selectedColumns, finalReportName);
    }
  };

  const sanitizeFilename = (filename: string): string => {
    return filename.replace(/\s+/g, ' ').substring(0, 100);
  };

  const handleReportNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const sanitized = sanitizeFilename(e.target.value);
    setReportName(sanitized);
  };

  const handleClose = () => {
    // Prevent closing while export API call is in progress
    if (hasInitiatedExport && isExporting) {
      return;
    }
    onClose();
  };

  const handleExited = () => {
    setAvailableColumns(allColumns);
    setSelectedColumns([]);
    setReportName('');
    setHasInitiatedExport(false);
    setExportCompleted(false);
  };

  const handleViewSavedReports = () => {
    handleClose();
    navigate('/saved-reports');
  };

  const dimensionCols = availableColumns.filter((col) => col.column_type === 'dimension');
  const valueCols = availableColumns.filter((col) => col.column_type === 'value');
  const availableDisplayColumns = [...dimensionCols, ...valueCols];

  // Loading state
  if (isLoading) {
    return (
      <StyledModal
        show={show}
        onHide={handleClose}
        onExited={handleExited}
        centered={!isMobile}
        size="xl"
        $isMobile={isMobile}
      >
        <ModalHeader $isDark={isDark}>
          <ModalTitle $isDark={isDark}>
            <BsFiletypeCsv size={18} />
            <span>Custom Export</span>
          </ModalTitle>
          <CloseButton onClick={handleClose} $isMobile={isMobile}>
            <FiX size={20} />
          </CloseButton>
        </ModalHeader>
        <ModalBody>
          <LoadingContainer>
            <Spinner animation="border" />
            <LoadingText>Loading export columns...</LoadingText>
          </LoadingContainer>
        </ModalBody>
      </StyledModal>
    );
  }

  // Error state
  if (error) {
    return (
      <StyledModal
        show={show}
        onHide={handleClose}
        onExited={handleExited}
        centered={!isMobile}
        size="xl"
        $isMobile={isMobile}
      >
        <ModalHeader $isDark={isDark}>
          <ModalTitle $isDark={isDark}>
            <BsFiletypeCsv size={18} />
            <span>Custom Export</span>
          </ModalTitle>
          <CloseButton onClick={handleClose} $isMobile={isMobile}>
            <FiX size={20} />
          </CloseButton>
        </ModalHeader>
        <ModalBody>
          <ErrorContainer>
            <FiAlertCircle size={48} />
            <ErrorText>{error}</ErrorText>
            <RetryButton onClick={fetchColumns}>Try Again</RetryButton>
          </ErrorContainer>
        </ModalBody>
      </StyledModal>
    );
  }

  // Export in progress or completed state
  if (hasInitiatedExport) {
    const isLocked = isExporting && !exportCompleted; // Lock modal only during API call
    return (
      <StyledModal
        show={show}
        onHide={handleClose}
        onExited={handleExited}
        centered
        size="sm"
        backdrop={isLocked ? 'static' : true}
        keyboard={!isLocked}
        $isCompact
      >
        <ModalHeader $isDark={isDark}>
          <ModalTitle $isDark={isDark}>
            <BsFiletypeCsv size={18} />
            <span>Custom Export</span>
          </ModalTitle>
          {!isLocked && (
            <CloseButton onClick={handleClose}>
              <FiX size={20} />
            </CloseButton>
          )}
        </ModalHeader>

        <ModalBody>
          {exportCompleted ? (
            <ReadySection>
              <ReadyContent>
                <IoDownload size={32} />
                <ReadyText>
                  <h4>Your Export is Ready</h4>
                  <p>
                    The <strong>{reportName}</strong> report has been generated and is ready to
                    download. We have also sent an email with this file.
                  </p>
                </ReadyText>
              </ReadyContent>
            </ReadySection>
          ) : (
            <ProgressSection>
              <ProgressContent>
                <CustomSpinner />
                <ProgressText>
                  <h4>Export In Progress</h4>
                  <p>You&apos;ll receive an email once the export is complete.</p>
                </ProgressText>
              </ProgressContent>
            </ProgressSection>
          )}
        </ModalBody>

        <ModalFooter>
          <ExportingFooterContent>
            {exportCompleted ? (
              <ViewSavedReportsButton onClick={handleViewSavedReports}>
                View Saved Reports
              </ViewSavedReportsButton>
            ) : (
              <>
                <ExportButton disabled>Processing...</ExportButton>
                <ExportHelpText>
                  You can close this dialog, you will receive a notification when export is ready.
                </ExportHelpText>
              </>
            )}
          </ExportingFooterContent>
        </ModalFooter>
      </StyledModal>
    );
  }

  // ── Main dialog ──

  return (
    <StyledModal
      show={show}
      onHide={handleClose}
      onExited={handleExited}
      centered={!isMobile}
      size="xl"
      $isMobile={isMobile}
    >
      <ModalHeader $isDark={isDark}>
        <ModalTitle $isDark={isDark}>
          <BsFiletypeCsv size={18} />
          <span>Custom Export</span>
        </ModalTitle>
        <CloseButton onClick={handleClose} $isMobile={isMobile}>
          <FiX size={20} />
        </CloseButton>
      </ModalHeader>

      {isMobile ? (
        /* ────── Mobile layout: single scrollable list ────── */
        <>
          <MobileBody>
            <MobileReportSection>
              <FilenameLabel>Report Name</FilenameLabel>
              <FilenameInput
                type="text"
                value={reportName}
                onChange={handleReportNameChange}
                placeholder="Enter report name"
                maxLength={100}
              />
            </MobileReportSection>

            {/* Selected columns section */}
            <MobileSection data-bucket="selected">
              <MobileSectionHeader>
                <MobileSectionTitle>
                  Selected
                  <MobileBadge>{selectedColumns.length}</MobileBadge>
                </MobileSectionTitle>
                {selectedColumns.length > 0 && (
                  <MobileTextButton onClick={handleRemoveAll}>Clear All</MobileTextButton>
                )}
              </MobileSectionHeader>

              {selectedColumns.length === 0 ? (
                <MobileEmptyState>Tap columns below to add</MobileEmptyState>
              ) : (
                <>
                  <MobileDragHint>Hold &amp; drag to reorder</MobileDragHint>
                  {selectedColumns.map((column, index) => (
                    <MobileSelectedItem
                      key={column.id}
                      data-column-index={index}
                      onTouchStart={(e) => handleTouchStart(e, column, 'selected')}
                      onTouchMove={handleTouchMove}
                      onTouchEnd={handleTouchEnd}
                      $isDragging={draggedItem?.id === column.id}
                      $isDragOver={dragOverItemIndex === index}
                    >
                      <MobileDragHandle>
                        <FiMove size={14} />
                      </MobileDragHandle>
                      <ColumnOrder>{index + 1}</ColumnOrder>
                      <MobileColumnLabel>{column.label}</MobileColumnLabel>
                      <MobileActionButton
                        $variant="remove"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveColumn(column);
                        }}
                      >
                        <FiX size={16} />
                      </MobileActionButton>
                    </MobileSelectedItem>
                  ))}
                </>
              )}
            </MobileSection>

            {/* Available columns section */}
            <MobileSection data-bucket="available">
              <MobileSectionHeader>
                <MobileSectionTitle>
                  Available
                  <MobileBadge>{availableColumns.length}</MobileBadge>
                </MobileSectionTitle>
                {availableColumns.length > 0 && (
                  <MobileTextButton onClick={handleAddAll}>Add All</MobileTextButton>
                )}
              </MobileSectionHeader>

              {availableColumns.length === 0 ? (
                <MobileEmptyState>All columns selected</MobileEmptyState>
              ) : (
                availableDisplayColumns.map((column) => (
                  <MobileAvailableItem
                    key={column.id}
                    onClick={() => handleAddColumn(column)}
                  >
                    <MobileColumnLabel>{column.label}</MobileColumnLabel>
                    <MobileActionButton $variant="add">
                      <FiPlus size={16} />
                    </MobileActionButton>
                  </MobileAvailableItem>
                ))
              )}
            </MobileSection>
          </MobileBody>

          <MobileFooter>
            <FooterInfo>
              {selectedColumns.length > 0 ? (
                <span>
                  <strong>{selectedColumns.length}</strong> column(s) to export
                </span>
              ) : (
                <span>Select at least one column</span>
              )}
            </FooterInfo>
            <MobileFooterActions>
              <CancelButton onClick={handleClose}>Cancel</CancelButton>
              <ExportButton
                onClick={handleExport}
                disabled={selectedColumns.length === 0 || !reportName.trim() || hasInitiatedExport}
              >
                {hasInitiatedExport ? 'Exporting...' : 'Export'}
              </ExportButton>
            </MobileFooterActions>
          </MobileFooter>
        </>
      ) : (
        /* ────── Desktop layout: two-bucket column picker ────── */
        <>
          <ModalBody>
            <ColumnsContainer>
              <ColumnBucket
                data-bucket="available"
                onDragOver={(e) => handleDragOverBucket(e, 'available')}
                onDrop={handleDropOnAvailable}
              >
                <BucketHeader>
                  <BucketTitle>Columns to Export</BucketTitle>
                  <BucketCount>{availableColumns.length} columns</BucketCount>
                </BucketHeader>
                <BucketContent>
                  {availableDisplayColumns.length > 0 && (
                    <ColumnGroup>
                      {availableDisplayColumns.map((column, index) => (
                        <ColumnItem
                          key={column.id}
                          data-column-index={index}
                          draggable
                          onDragStart={(e) => handleDragStart(e, column, 'available')}
                          onDragEnd={handleDragEnd}
                          onTouchStart={(e) => handleTouchStart(e, column, 'available')}
                          onTouchMove={handleTouchMove}
                          onTouchEnd={handleTouchEnd}
                          $isDragging={draggedItem?.id === column.id}
                          onClick={() => handleAddColumn(column)}
                        >
                          <ColumnInfo>
                            <ColumnName>{column.label}</ColumnName>
                          </ColumnInfo>
                          <ColumnActions>
                            <ActionIcon>
                              <FiArrowRight size={14} />
                            </ActionIcon>
                          </ColumnActions>
                        </ColumnItem>
                      ))}
                    </ColumnGroup>
                  )}

                  {availableColumns.length === 0 && <EmptyState>All columns selected</EmptyState>}
                </BucketContent>
              </ColumnBucket>

              <MiddleActions>
                <MiddleButton onClick={handleAddAll} disabled={availableColumns.length === 0}>
                  <FiArrowRight size={16} />
                  <span>Add All</span>
                </MiddleButton>
                <MiddleButton onClick={handleRemoveAll} disabled={selectedColumns.length === 0}>
                  <FiArrowLeft size={16} />
                  <span>Remove All</span>
                </MiddleButton>
              </MiddleActions>

              <ColumnBucket
                $isSelected
                data-bucket="selected"
                onDragOver={(e) => handleDragOverBucket(e, 'selected')}
                onDrop={handleDropOnSelected}
              >
                <BucketHeader>
                  <BucketTitle>Selected Columns</BucketTitle>
                  <BucketCount>{selectedColumns.length} columns</BucketCount>
                </BucketHeader>
                <BucketContent>
                  <HelpText>Drag to reorder columns</HelpText>
                  {selectedColumns.map((column, index) => (
                    <ColumnItem
                      key={column.id}
                      data-column-index={index}
                      draggable
                      onDragStart={(e) => handleDragStart(e, column, 'selected')}
                      onDragEnd={handleDragEnd}
                      onDragOver={(e) => handleDragOverItem(e, index)}
                      onDrop={(e) => handleDropOnSelectedItem(e, index)}
                      onTouchStart={(e) => handleTouchStart(e, column, 'selected')}
                      onTouchMove={handleTouchMove}
                      onTouchEnd={handleTouchEnd}
                      $isDragging={draggedItem?.id === column.id}
                      $isDragOver={dragOverItemIndex === index}
                      onClick={() => handleRemoveColumn(column)}
                    >
                      <DragHandle>
                        <FiMove size={14} />
                      </DragHandle>
                      <ColumnInfo>
                        <ColumnOrder>{index + 1}</ColumnOrder>
                        <ColumnName>{column.label}</ColumnName>
                      </ColumnInfo>
                      <ColumnActions>
                        <ActionIcon $isRemove>
                          <FiArrowLeft size={14} />
                        </ActionIcon>
                      </ColumnActions>
                    </ColumnItem>
                  ))}
                  {selectedColumns.length === 0 && (
                    <EmptyState>Drag columns here or click to add</EmptyState>
                  )}
                </BucketContent>
              </ColumnBucket>
            </ColumnsContainer>
          </ModalBody>

          <ModalFooter>
            <FooterContent>
              <FilenameSection>
                <FilenameLabel>Report Name</FilenameLabel>
                <FilenameInput
                  type="text"
                  value={reportName}
                  onChange={handleReportNameChange}
                  placeholder="Enter report name"
                  maxLength={100}
                />
                <CharacterCount>{reportName.length}/100</CharacterCount>
              </FilenameSection>
              <FooterBottom>
                <FooterInfo>
                  {selectedColumns.length > 0 ? (
                    <span>
                      <strong>{selectedColumns.length}</strong> column(s) to export
                    </span>
                  ) : (
                    <span>Select at least one column to export</span>
                  )}
                </FooterInfo>
                <FooterActions>
                  <CancelButton onClick={handleClose}>Cancel</CancelButton>
                  <ExportButton
                    onClick={handleExport}
                    disabled={
                      selectedColumns.length === 0 || !reportName.trim() || hasInitiatedExport
                    }
                  >
                    {hasInitiatedExport ? 'Exporting...' : 'Export'}
                  </ExportButton>
                </FooterActions>
              </FooterBottom>
            </FooterContent>
          </ModalFooter>
        </>
      )}
    </StyledModal>
  );
};

export default CustomExportDialog;

// ═══════════════════════════════════════════
// Styled Components — Shared
// ═══════════════════════════════════════════

const StyledModal = styled(Modal)<{ $isCompact?: boolean; $isMobile?: boolean }>`
  .modal-dialog {
    ${({ $isMobile, $isCompact }) =>
      $isMobile && !$isCompact
        ? css`
            margin: 0;
            max-width: 100%;
            width: 100%;
            min-height: 100vh;
            min-height: 100dvh;
          `
        : css`
            max-width: ${$isCompact ? '500px' : '900px'};
          `}
  }

  .modal-content {
    position: relative;
    background: ${({ theme }) => theme.dropdown.background_color};
    border: 1px solid ${({ theme }) => theme.border_color};

    ${({ $isMobile, $isCompact }) =>
      $isMobile && !$isCompact
        ? css`
            border-radius: 0;
            border: none;
            min-height: 100vh;
            min-height: 100dvh;
            display: flex;
            flex-direction: column;
          `
        : css`
            border-radius: 12px;
            overflow: visible;
          `}
  }
`;

const ModalHeader = styled.div<{ $isDark: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 24px 0 24px;
`;

const ModalTitle = styled.h3<{ $isDark: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  line-height: 1.2;
  color: ${({ $isDark }) => ($isDark ? '#f9fafb' : '#374151')};

  svg {
    flex-shrink: 0;
  }
`;

const CloseButton = styled.button<{ $isMobile?: boolean }>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #6b7280;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  z-index: 10;

  ${({ $isMobile }) =>
    $isMobile
      ? css`
          position: relative;
          top: auto;
          right: auto;
          flex-shrink: 0;
        `
      : css`
          position: absolute;
          top: -20px;
          right: -20px;
        `}

  svg {
    width: 20px;
    height: 20px;
    color: white;
  }

  &:hover {
    background: #4b5563;
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const ModalBody = styled.div`
  padding: 20px;
`;

const ModalFooter = styled.div`
  padding: 16px 20px;
  border-top: 1px solid ${({ theme }) => theme.border_color};
`;

const FilenameLabel = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.text};
`;

const FilenameInput = styled.input`
  padding: 10px 12px;
  border: 1px solid ${({ theme }) => theme.border_color};
  border-radius: 6px;
  background: ${({ theme }) => theme.dropdown.background_color};
  color: ${({ theme }) => theme.text};
  font-size: 1rem;
  font-family: inherit;
  transition: all 0.15s ease;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.primary_color};
  }

  &::placeholder {
    color: ${({ theme }) => theme.text};
    opacity: 0.5;
  }
`;

const FooterInfo = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.text};

  strong {
    color: ${({ theme }) => theme.primary_color};
  }
`;

const CancelButton = styled.button`
  padding: 10px 20px;
  border: 1px solid ${({ theme }) => theme.border_color};
  border-radius: 6px;
  background: transparent;
  color: ${({ theme }) => theme.text};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: ${({ theme }) => theme.button_plain.hover};
  }
`;

const ExportButton = styled.button`
  padding: 10px 24px;
  border: none;
  border-radius: 6px;
  background: ${({ theme }) => theme.primary_color};
  color: white;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover:not(:disabled) {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ColumnOrder = styled.span`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  background: ${({ theme }) => theme.primary_color};
  color: white;
  font-size: 11px;
  font-weight: 600;
  flex-shrink: 0;
`;

// ═══════════════════════════════════════════
// Styled Components — Desktop only
// ═══════════════════════════════════════════

const ColumnsContainer = styled.div`
  display: flex;
  gap: 16px;
  align-items: stretch;
  min-height: 450px;
`;

const ColumnBucket = styled.div<{ $isSelected?: boolean }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  border: 2px solid
    ${({ theme, $isSelected }) => ($isSelected ? theme.primary_color : theme.border_color)};
  border-radius: 8px;
  background: ${({ theme }) => theme.dropdown.background_color};
  overflow: hidden;
`;

const BucketHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: ${({ theme }) => theme.button_plain.hover};
  border-bottom: 1px solid ${({ theme }) => theme.border_color};
`;

const BucketTitle = styled.h4`
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: ${({ theme }) => theme.text};
`;

const BucketCount = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.text};
  opacity: 0.7;
`;

const BucketContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0px;
  max-height: 400px;
`;

const ColumnGroup = styled.div`
  margin-bottom: 16px;
`;

const ColumnItem = styled.div<{
  $isDragging?: boolean;
  $isDragOver?: boolean;
}>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  margin-bottom: 4px;
  background: ${({ theme, $isDragging, $isDragOver }) =>
    $isDragOver
      ? theme.primary_color + '20'
      : $isDragging
        ? theme.button_plain.hover
        : 'transparent'};
  border: 1px solid
    ${({ theme, $isDragOver }) => ($isDragOver ? theme.primary_color : 'transparent')};
  cursor: pointer;
  transition: all 0.15s ease;
  opacity: ${({ $isDragging }) => ($isDragging ? 0.5 : 1)};

  &:hover {
    background: ${({ theme }) => theme.button_plain.hover};
  }
`;

const DragHandle = styled.div`
  color: ${({ theme }) => theme.text};
  opacity: 0.5;
  cursor: grab;

  &:active {
    cursor: grabbing;
  }
`;

const ColumnInfo = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ColumnName = styled.span`
  font-size: 1rem;
  font-weight: 500;
  color: ${({ theme }) => theme.text};
`;

const ColumnActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ActionIcon = styled.div<{ $isRemove?: boolean }>`
  color: ${({ theme, $isRemove }) => ($isRemove ? '#ef4444' : theme.primary_color)};
  opacity: 0.7;
`;

const MiddleActions = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 8px;
  padding: 0 8px;
`;

const MiddleButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border: 1px solid ${({ theme }) => theme.border_color};
  border-radius: 6px;
  background: ${({ theme }) => theme.dropdown.background_color};
  color: ${({ theme }) => theme.text};
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.button_plain.hover};
    border-color: ${({ theme }) => theme.primary_color};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100px;
  color: ${({ theme }) => theme.text};
  opacity: 0.5;
  font-size: 13px;
`;

const HelpText = styled.div`
  padding: 8px 12px;
  margin-bottom: 8px;
  border-radius: 4px;
  background: ${({ theme }) => theme.button_plain.hover};
  color: ${({ theme }) => theme.text};
  opacity: 0.7;
  font-size: 11px;
  text-align: center;
`;

const FooterContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FilenameSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const CharacterCount = styled.span`
  font-size: 11px;
  color: ${({ theme }) => theme.text};
  opacity: 0.6;
  text-align: right;
`;

const FooterBottom = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const FooterActions = styled.div`
  display: flex;
  gap: 12px;
`;

// ═══════════════════════════════════════════
// Styled Components — Mobile only
// ═══════════════════════════════════════════

const MobileBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  -webkit-overflow-scrolling: touch;
`;

const MobileReportSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 20px;
`;

const MobileSection = styled.div`
  margin-bottom: 16px;
`;

const MobileSectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 0;
  border-bottom: 1px solid ${({ theme }) => theme.border_color};
  margin-bottom: 4px;
`;

const MobileSectionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.text};
`;

const MobileBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 22px;
  padding: 0 6px;
  border-radius: 11px;
  background: ${({ theme }) => theme.primary_color};
  color: white;
  font-size: 12px;
  font-weight: 600;
`;

const MobileTextButton = styled.button`
  padding: 4px 10px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: ${({ theme }) => theme.primary_color};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;

  &:active {
    opacity: 0.7;
  }
`;

const MobileEmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 16px;
  color: ${({ theme }) => theme.text};
  opacity: 0.45;
  font-size: 13px;
`;

const MobileDragHint = styled.div`
  padding: 6px 12px;
  color: ${({ theme }) => theme.text};
  opacity: 0.5;
  font-size: 11px;
  text-align: center;
`;

const mobileItemBase = css`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px;
  border-radius: 8px;
  margin-bottom: 4px;
  transition: background 0.15s ease;
`;

const MobileSelectedItem = styled.div<{
  $isDragging?: boolean;
  $isDragOver?: boolean;
}>`
  ${mobileItemBase}
  background: ${({ theme, $isDragging, $isDragOver }) =>
    $isDragOver
      ? theme.primary_color + '20'
      : $isDragging
        ? theme.button_plain.hover
        : 'transparent'};
  border: 1px solid
    ${({ theme, $isDragOver }) => ($isDragOver ? theme.primary_color : 'transparent')};
  opacity: ${({ $isDragging }) => ($isDragging ? 0.5 : 1)};
`;

const MobileAvailableItem = styled.div`
  ${mobileItemBase}
  cursor: pointer;

  &:active {
    background: ${({ theme }) => theme.button_plain.hover};
  }
`;

const MobileDragHandle = styled.div`
  color: ${({ theme }) => theme.text};
  opacity: 0.4;
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

const MobileColumnLabel = styled.span`
  flex: 1;
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.text};
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const MobileActionButton = styled.button<{ $variant: 'add' | 'remove' }>`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.15s ease;

  ${({ $variant, theme }) =>
    $variant === 'add'
      ? css`
          background: ${theme.primary_color}18;
          color: ${theme.primary_color};

          &:active {
            background: ${theme.primary_color}30;
          }
        `
      : css`
          background: rgba(239, 68, 68, 0.1);
          color: #ef4444;

          &:active {
            background: rgba(239, 68, 68, 0.2);
          }
        `}
`;

const MobileFooter = styled.div`
  padding: 12px 16px;
  padding-bottom: max(12px, env(safe-area-inset-bottom));
  border-top: 1px solid ${({ theme }) => theme.border_color};
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex-shrink: 0;
`;

const MobileFooterActions = styled.div`
  display: flex;
  gap: 10px;

  > button {
    flex: 1;
  }
`;

// ═══════════════════════════════════════════
// Styled Components — Loading / Error / Progress states
// ═══════════════════════════════════════════

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  gap: 16px;
`;

const LoadingText = styled.span`
  color: ${({ theme }) => theme.text};
  font-size: 14px;
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  gap: 16px;
  color: #ef4444;
`;

const ErrorText = styled.span`
  color: ${({ theme }) => theme.text};
  font-size: 14px;
  text-align: center;
`;

const RetryButton = styled.button`
  padding: 8px 16px;
  border: 1px solid ${({ theme }) => theme.primary_color};
  border-radius: 6px;
  background: transparent;
  color: ${({ theme }) => theme.primary_color};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: ${({ theme }) => theme.primary_color};
    color: white;
  }
`;

const CustomSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid rgba(16, 185, 129, 0.2);
  border-left: 4px solid #10b981;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  flex-shrink: 0;

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

const ProgressSection = styled.div`
  background: #5a6c7d;
  border-radius: 8px;
  padding: 40px 24px;
  margin: 16px 0;
`;

const ProgressContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 24px;
`;

const ProgressText = styled.div`
  h4 {
    margin: 0 0 12px 0;
    font-size: 18px;
    font-weight: 600;
    color: white;
  }

  p {
    margin: 0 0 12px 0;
    font-size: 14px;
    color: #e5e7eb;
    line-height: 1.5;
  }
`;

const ExportingFooterContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`;

const ExportHelpText = styled.p`
  margin: 0;
  font-size: 13px;
  color: ${({ theme }) => theme.text};
  text-align: center;
  line-height: 1.4;
  opacity: 0.8;
`;

const ReadySection = styled.div`
  background: ${({ theme }) => theme.button_plain?.hover || '#f3f4f6'};
  border-radius: 8px;
  padding: 40px 24px;
  margin: 16px 0;
`;

const ReadyContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 24px;

  svg {
    color: ${({ theme }) => theme.text};
    opacity: 0.7;
  }
`;

const ReadyText = styled.div`
  h4 {
    margin: 0 0 12px 0;
    font-size: 18px;
    font-weight: 600;
    color: ${({ theme }) => theme.text};
  }

  p {
    margin: 0;
    font-size: 14px;
    color: ${({ theme }) => theme.text};
    opacity: 0.8;
    line-height: 1.5;
  }

  strong {
    font-weight: 600;
  }
`;

const ViewSavedReportsButton = styled.button`
  width: 100%;
  padding: 12px 20px;
  border: none;
  border-radius: 10px;
  background: #609954;
  color: white;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: #4f7d44;
  }
`;